/**
 * Wire message type tags. Stable on the wire — append, never renumber.
 * Client→server use the low range; server→client the high range.
 */
export const Msg = {
  // client → server
  Hello: 0x10,
  Input: 0x01,
  Fire: 0x02,
  Throw: 0x03,
  // server → client
  Welcome: 0x90,
  Snapshot: 0x81,
  HitConfirm: 0x82,
  Death: 0x83,
  Score: 0x84,
  Match: 0x85,
  Damaged: 0x86,
  Shot: 0x87,
  Explosion: 0x88,
  Roster: 0x89,
  MatchSummary: 0x8a,
} as const;

/** Server-simulated projectile kinds (rendered by clients from snapshots). */
export const ProjectileKind = {
  Grenade: 0,
  Rocket: 1,
} as const;

/**
 * Hand-grenade variants (only meaningful when ProjectileKind.Grenade). Drives
 * the throw effect (frag = splash damage, smoke = vision cloud, flashbang =
 * blind + aim sway) and the rendered look.
 */
export const GrenadeType = {
  Frag: 0,
  Smoke: 1,
  Flashbang: 2,
} as const;

/** Per-entity state flags packed into one snapshot byte. */
export const EntityFlag = {
  OnGround: 1 << 0,
  Crouching: 1 << 1,
  Alive: 1 << 2,
  Reloading: 1 << 3,
  Protected: 1 << 4, // spawn protection — cannot take damage
} as const;

/** Match loop states. */
export const MatchState = {
  Playing: 0,
  Intermission: 1,
  Starting: 2, // frozen 3-2-1 countdown before a match; only look is allowed
} as const;

export interface HelloMsg {
  type: typeof Msg.Hello;
  preferredTeam: number; // 0/1, or 255 = no preference
  /** Requested display name (server sanitizes + de-duplicates). */
  name: string;
}

/** Per-player flags carried in roster entries. */
export const RosterFlag = {
  Bot: 1 << 0,
} as const;

export interface RosterEntry {
  id: number;
  teamId: number;
  kills: number;
  deaths: number;
  flags: number; // RosterFlag bits
  name: string;
}

/**
 * The full player roster (names + K/D). Broadcast on join/leave/kill and at
 * match reset — drives the kill feed names and the Tab scoreboard.
 */
export interface RosterMsg {
  type: typeof Msg.Roster;
  players: RosterEntry[];
}

export interface InputMsg {
  type: typeof Msg.Input;
  seq: number;
  buttons: number;
  yaw: number;
  pitch: number;
  weaponId: number; // the gun the client currently has equipped (server validates Fire against it)
}

/**
 * A fire intent. The server uses the shooter's own authoritative position as
 * the ray origin (anti-spoof) and trusts only the aim direction. `renderTick`
 * is the server tick the client was rendering (it interpolates ~2 snapshots in
 * the past), used to rewind targets for lag compensation.
 */
export interface FireMsg {
  type: typeof Msg.Fire;
  seq: number;
  renderTick: number;
  dx: number;
  dy: number;
  dz: number;
  weaponId: number;
}

export interface HitConfirmMsg {
  type: typeof Msg.HitConfirm;
  victimId: number;
  dmg: number;
  lethal: boolean;
}

export interface DeathMsg {
  type: typeof Msg.Death;
  victimId: number;
  killerId: number;
  weaponId: number;
  headshot: boolean;
  friendlyFire: boolean;
}

/** Per-team score broadcast (team deathmatch). Scores may go negative (FF penalty). */
export interface ScoreMsg {
  type: typeof Msg.Score;
  team0: number;
  team1: number;
}

/** Match loop state: kill target, time left, and the winner during intermission. */
export interface MatchMsg {
  type: typeof Msg.Match;
  state: number; // MatchState
  msLeft: number; // time left in the current state
  killTarget: number;
  winnerTeam: number; // 255 = none/draw
  // Per-match environmental wind (regenerated each match). `windDir` is the
  // world yaw the wind blows TOWARD (same sin/cos convention as aim yaw);
  // `windSpeed` is its strength in world units/second. Drives smoke drift and
  // the HUD wind gauge.
  windDir: number;
  windSpeed: number;
}

/** One player's frozen end-of-match stats line (for the leaderboard). */
export interface MatchSummaryRow {
  teamId: number;
  score: number;
  kills: number;
  deaths: number;
  assists: number;
  weaponId: number; // last-equipped weapon, for the [SMG]/[SG]… tag
  flags: number; // RosterFlag bits (e.g. Bot)
  name: string;
}

/**
 * The full end-of-round leaderboard, frozen at match end and broadcast once on
 * the transition to Intermission (also sent to anyone who joins mid-intermission).
 * Self-contained: drives the entire results board without consulting the live
 * roster (which keeps mutating as players join/leave between matches).
 */
export interface MatchSummaryMsg {
  type: typeof Msg.MatchSummary;
  winnerTeam: number; // 0/1, or 255 = draw
  score0: number; // team-0 (Fisura) final score
  score1: number; // team-1 (Police) final score
  matchId: string; // e.g. "#PHASE0-00013"
  durationMs: number;
  totalShots: number;
  totalHits: number;
  totalKills: number;
  totalDeaths: number;
  totalAssists: number;
  longestStreak: number;
  longestStreakName: string;
  rows: MatchSummaryRow[];
}

/**
 * Sent to a victim when they take damage, so the client can show a directional
 * hit indicator. `dirYaw` is the world yaw from victim toward the attacker.
 */
export interface DamagedMsg {
  type: typeof Msg.Damaged;
  dmg: number;
  dirYaw: number;
}

/**
 * Broadcast whenever any player fires, so other clients can render the shot —
 * positional gunfire audio, tracer and muzzle flash. Clients ignore their own
 * shooterId (they already render local effects).
 */
export interface ShotMsg {
  type: typeof Msg.Shot;
  shooterId: number;
  weaponId: number;
  x: number; // muzzle/eye origin
  y: number;
  z: number;
  dx: number; // (already perturbed) fire direction
  dy: number;
  dz: number;
}

export interface WelcomeMsg {
  type: typeof Msg.Welcome;
  entityId: number;
  teamId: number;
  tickHz: number;
  snapHz: number;
}

export interface SnapshotEntity {
  id: number;
  teamId: number;
  flags: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  // Velocity is included so the receiving client can reconcile its own player
  // by replaying unacked inputs from this authoritative state.
  vx: number;
  vy: number;
  vz: number;
  health: number; // 0–100
  pitch: number; // aim pitch, for third-person torso/spine aiming
  weaponId: number; // equipped weapon, for third-person model/anim
  suppression: number; // 0–255: how suppressed this player currently is
  ammo: number; // rounds left in the mag (authoritative; clients mirror this)
  blind: number; // 0–255: flashbang white-out intensity (own-client overlay)
}

/** A live server-simulated projectile (grenade/rocket) included in snapshots. */
export interface SnapshotProjectile {
  id: number;
  kind: number; // ProjectileKind
  gtype: number; // GrenadeType (grenades only; 0 for rockets)
  x: number;
  y: number;
  z: number;
}

export interface SnapshotMsg {
  type: typeof Msg.Snapshot;
  serverTick: number;
  ackSeq: number; // last input seq processed for the receiving client
  entities: SnapshotEntity[];
  projectiles: SnapshotProjectile[];
}

/** Grenade throw intent (server spawns/owns the projectile). */
export interface ThrowMsg {
  type: typeof Msg.Throw;
  seq: number;
  dx: number;
  dy: number;
  dz: number;
  gtype: number; // GrenadeType selected (server validates against the player's stock)
}

/** An explosion happened (grenade/rocket) — clients render VFX/audio/shake. */
export interface ExplosionMsg {
  type: typeof Msg.Explosion;
  kind: number; // ProjectileKind
  gtype: number; // GrenadeType (grenades only; 0 for rockets) — picks the VFX
  x: number;
  y: number;
  z: number;
}

export type AnyMessage =
  | HelloMsg
  | InputMsg
  | FireMsg
  | WelcomeMsg
  | SnapshotMsg
  | HitConfirmMsg
  | DeathMsg
  | ScoreMsg
  | MatchMsg
  | DamagedMsg
  | ShotMsg
  | ThrowMsg
  | ExplosionMsg
  | RosterMsg
  | MatchSummaryMsg;
